import { FC, useState } from 'react';
import { Box, Button, ButtonProps } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { GridActionsCellItem } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';

import { Jobseeker, Resume } from '@libs/models';
import { baseFileQuery } from '@carcareers/store';
import { useSnackbar } from '@carcareers/contexts';

interface DownloadResumeButtonProps extends ButtonProps {
  jobseeker?: Jobseeker;
  resume?: Resume;
  showViewButton?: boolean;
  showInMenu?: boolean;
}

export const DownloadResumeButton: FC<DownloadResumeButtonProps> = ({
  jobseeker,
  resume,
  showViewButton = false,
  showInMenu = false,
  ...props
}) => {
  const { toggleSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (!jobseeker?.resume?.id && !resume?.id) {
      toggleSnackbar('error', 'No resume found for this jobseeker.');
      return;
    }

    setIsDownloading(true);

    try {
      const resumeId = jobseeker?.resume?.id ?? resume?.id ?? 0;
      const response = await baseFileQuery({
        url: `/resume/${resumeId}/stream`,
        method: 'GET',
        headers: {
          Accept:
            'application/octet-stream, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        },
      });

      if (!response.ok) {
        toggleSnackbar('error', 'Error downloading resume. Please try again later.');
        setIsDownloading(false);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const firstName = jobseeker?.user?.firstName || '';
      const lastName = jobseeker?.user?.lastName || '';
      const filename = `Resume${firstName}${lastName}`;

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      toggleSnackbar('error', 'Error downloading resume. Please try again later.');
    } finally {
      setIsDownloading(false);
    }
  };

  if (showInMenu) {
    const resumeId = jobseeker?.resume?.id ?? resume?.id;

    return (
      <GridActionsCellItem
        showInMenu
        key={`download-${resumeId}`}
        label={isDownloading ? 'Downloading...' : 'Download'}
        icon={<DownloadIcon />}
        onClick={handleDownload}
        disabled={isDownloading}
      />
    );
  }

  return (
    <Box display="flex" gap={1}>
      <Button
        variant="contained"
        color="primary"
        startIcon={<DownloadIcon />}
        onClick={handleDownload}
        loading={isDownloading}
        disabled={isDownloading}
        sx={{ alignSelf: 'flex-start' }}
        {...props}
      >
        {isDownloading ? 'Downloading...' : 'Download Resume'}
      </Button>
      {showViewButton && (
        <Button
          variant="text"
          color="primary"
          sx={{ alignSelf: 'flex-start' }}
          onClick={() => {
            navigate(`/employer/candidates/${jobseeker?.id}/resume`);
          }}
        >
          View Resume
        </Button>
      )}
    </Box>
  );
};

export default DownloadResumeButton;
